import { QueryClient, QueryKey } from "@tanstack/react-query";
import PagedResult from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/core/PagedResult";
import { queryClient as appQueryClient } from "../queryClient";

/**
 * Bridge between TableBasedCrud and the app QueryClient.
 *
 * TableBasedCrud (submodule) still owns when to load: it calls onDataFetch
 * (page, rowsPerPage, sortBy?, sortDir?) => Promise<PagedResult<T>> and
 * re-runs that when lastUpdateTimestamp / forceUpdateOn change. This factory
 * returns that callback, implemented with fetchQuery, so a table can share
 * cache, in-flight de-duplication and invalidation without changing the
 * component.
 *
 * Wire it with the existing client (`queryClient.ts` or useQueryClient() in
 * components), unwrap() so failed envelopes reject, and QueryKeyFactory so
 * list keys stay hierarchical. A second QueryClient or hand-built key arrays
 * would miss invalidateQueries.
 *
 * App default staleTime is 0. Tables pass TABLE_QUERY_STALE_TIME so paging
 * can hit the cache. After a mutation, invalidateQueries then bump
 * forceUpdateOn. Invalidation alone does not redraw the table (it is not a
 * cache subscriber). A bump alone can keep serving rows until they go stale.
 *
 * queryFn should only return Promise<PagedResult<T>>. These loaders mix extra
 * work into the fetch and are not a template:
 * - useKnowledgeBaseList.setOnDataFetch: localStorage.removeItem inside it
 * - useWorkLogList used to return cached rows and fire the month request as a side effect.
 *   The list query now returns the page itself.
 * setState, storage and extra requests belong on the mutation / UI side.
 */

export const TABLE_QUERY_STALE_TIME = 60_000;

export type OnDataFetch<T> = (page: number, rowsPerPage: number, sortBy?: string, sortDir?: string) => Promise<PagedResult<T>>;

export type CreateTableQueryOptions<T> = {
  queryKey: (page: number, rowsPerPage: number, sortBy?: string, sortDir?: string) => QueryKey;
  queryFn: (page: number, rowsPerPage: number, sortBy?: string, sortDir?: string) => Promise<PagedResult<T>>;
  client?: QueryClient;
  staleTime?: number;
};

export function createTableQuery<T>(options: CreateTableQueryOptions<T>): OnDataFetch<T> {
  const client = options.client ?? appQueryClient;
  const staleTime = options.staleTime ?? TABLE_QUERY_STALE_TIME;

  return (page, rowsPerPage, sortBy, sortDir) =>
    client.fetchQuery({
      queryKey: options.queryKey(page, rowsPerPage, sortBy, sortDir),
      queryFn: () => options.queryFn(page, rowsPerPage, sortBy, sortDir),
      staleTime,
    });
}

/** Mark list queries stale, then bump TableBasedCrud so it re-calls onDataFetch. */
export async function refreshTableQuery(
  queryKey: QueryKey,
  bumpForceUpdateOn: () => void,
  client: QueryClient = appQueryClient,
): Promise<void> {
  await client.invalidateQueries({ queryKey });
  bumpForceUpdateOn();
}
